import {
  useMutation,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import get from "lodash/get";
import { z } from "zod";

import { queryClient } from "@/api/core";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import toast from "@/toast";
import { httpErrorMessages } from "@/utils/errors";
import {
  hasPages,
  isEntity,
  removePageItem,
  updatePageItem,
} from "@/utils/queries";

import type { Entity, EntitySchema } from "../types";

enum MutationActions {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
}

interface Options<T, E, V, C> extends UseMutationOptions<T, E, V, C> {
  /** The "id" attribute of the entity used in Variables (should be "id") */
  idAttributeFromVariables?: string;
  action: MutationActions;
  globalQueryKey: QueryKey;
  schema?: EntitySchema<Entity>;
  skipOnSuccess?: boolean;
  /** The updated Entity if the API response doesn't provide a full Entity (ie: no content 200) */
  setNextEntity?(variables: V): Entity | undefined;
}

interface MutationVariables {
  id: string;
  [key: string]: unknown;
}

function useEntityMutation<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
>(
  options: Options<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const {
    action,
    globalQueryKey,
    onSuccess,
    onError,
    skipOnSuccess = false,
    idAttributeFromVariables = "id",
    schema,
    setNextEntity,
    ...mutationOptions
  } = options;

  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();

  return useMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    onSuccess(response, variables, context) {
      if (onSuccess) {
        onSuccess(response, variables, context);
      }

      if (skipOnSuccess) {
        return;
      }

      const nextEntity: Entity | undefined = setNextEntity
        ? setNextEntity(variables)
        : (response as { data: Entity }).data;
      const isDeleting = action === MutationActions.DELETE;
      const isUpdating = action === MutationActions.UPDATE;

      const id = get(
        variables as object | null | undefined,
        idAttributeFromVariables,
      ) as string | undefined;

      if (isDeleting && id) {
        queryClient
          .getQueriesData<unknown>({ queryKey: globalQueryKey })
          .forEach(([cachedKey, cachedData]) => {
            if (hasPages(cachedData)) {
              // infinite pages
              removePageItem<Entity>(
                cachedKey,
                { id },
                (o, n) => String(o.id) === String(n.id),
              );
            } else if (Array.isArray(cachedData)) {
              // basic arrays
              queryClient.setQueryData<Entity[]>(
                cachedKey,
                (cachedData as Entity[]).filter(
                  (entity) => entity.id !== id,
                ),
              );
            } else if (isEntity(cachedData) && cachedData.id === id) {
              // single entities
              queryClient.removeQueries({ queryKey: cachedKey, exact: true });
              queryClient.cancelQueries({ queryKey: cachedKey, exact: true });
            }
          });
      } else if (isUpdating && id) {
        queryClient
          .getQueriesData<unknown>({ queryKey: globalQueryKey })
          .forEach(([cachedKey, cachedData]) => {
            const schemaToUse = (schema ??
              z.custom<Entity>()) as EntitySchema<Entity>;
            const parsedEntity = schemaToUse.safeParse(nextEntity);

            if (!parsedEntity.success) {
              return;
            }

            const parsedNextEntity = parsedEntity.data;

            if (hasPages(cachedData)) {
              // infinite pages
              updatePageItem<Entity>(
                cachedKey,
                parsedNextEntity,
                (o, n) => String(o.id) === String(n.id),
              );
            } else if (Array.isArray(cachedData)) {
              // basic arrays
              queryClient.setQueryData<Entity[]>(
                cachedKey,
                (cachedData as Entity[]).map((entity) => {
                  if (entity.id === id) {
                    return parsedNextEntity;
                  }

                  return entity;
                }),
              );
            } else if (isEntity(cachedData) && cachedData.id === id) {
              // single entities
              queryClient.setQueryData(cachedKey, parsedNextEntity);
            }
          });
      }
    },
    onError(error, variables, context) {
      if (onError) {
        onError(error, variables, context);
      }

      // If we have a network error, let's check to see if the user
      // requires SMS re-verification. If so, let's open the modal.
      if (axios.isAxiosError(error) && !!currentAccount) {
        const axiosError = error;
        const needsSmsReVerification =
          !!currentAccount.source.sms_reverification_required;

        if (needsSmsReVerification && axiosError.response?.status === 403) {
          openModal("REVERIFY_SMS");
        }

        if (axiosError.response?.status === 429) {
          const fallbackMsg = httpErrorMessages.find(
            (httpError) => httpError.code === 429,
          )?.description;
          const msg =
            typeof axiosError.response.data.error === "string"
              ? axiosError.response.data.error
              : fallbackMsg;
          toast.error(msg);
        }
      }
    },
  });
}

export { useEntityMutation, MutationActions, type MutationVariables };
